import React, { useCallback, useState } from 'react';
import { Calendar, Save, RefreshCw, Download, Clock, Briefcase, Plus, Minus, CalendarRange, User } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт вспомогательных функций
import { calculateDateDifference, getWorkingDaysBetween, addDays, formatDate } from '../../../../../utils/calculations/date';

/**
 * Компонент основной формы расчета промежутка времени
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  russianRegions,
  holidays,
  saveCalculation
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Очищаем результаты при смене типа расчета
      if (name === 'calculationType') {
        setResults(null);
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData, setResults]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    // Валидация общих полей
    if (formData.calculationType === 'standard' || formData.calculationType === 'working') {
      if (!formData.startDate) newErrors.startDate = 'Укажите начальную дату';
      if (!formData.endDate) newErrors.endDate = 'Укажите конечную дату';
      
      try {
        const startDateObj = new Date(formData.startDate);
        const endDateObj = new Date(formData.endDate);
        
        if (isNaN(startDateObj.getTime())) {
          newErrors.startDate = 'Некорректная дата';
        }
        
        if (isNaN(endDateObj.getTime())) {
          newErrors.endDate = 'Некорректная дата';
        }
        
        // Проверка, что конечная дата не раньше начальной
        if (!newErrors.startDate && !newErrors.endDate && endDateObj < startDateObj) {
          newErrors.endDate = 'Конечная дата должна быть позже начальной';
        }
      } catch (e) {
        newErrors.general = 'Ошибка при обработке дат';
      }
    }
    
    // Дополнительные проверки для рабочих дней
    if (formData.calculationType === 'working') {
      if (!formData.regionCode) newErrors.regionCode = 'Выберите регион';
    }
    
    // Проверки для добавления/вычитания дат
    if (formData.calculationType === 'add-subtract') {
      if (!formData.startDate) newErrors.startDate = 'Укажите дату';
      
      if (isNaN(parseInt(formData.datePeriod)) || parseInt(formData.datePeriod) <= 0) {
        newErrors.datePeriod = 'Укажите положительное число';
      }
      
      if (!formData.periodType) newErrors.periodType = 'Выберите тип периода';
    }
    
    // Проверки для расчета возраста
    if (formData.calculationType === 'age') {
      if (!formData.birthdayDate) newErrors.birthdayDate = 'Укажите дату рождения';
      if (!formData.referenceDate) newErrors.referenceDate = 'Укажите дату отсчета';
      
      try {
        const birthdayObj = new Date(formData.birthdayDate);
        const referenceObj = new Date(formData.referenceDate);
        
        if (isNaN(birthdayObj.getTime())) {
          newErrors.birthdayDate = 'Некорректная дата';
        }
        
        if (isNaN(referenceObj.getTime())) {
          newErrors.referenceDate = 'Некорректная дата';
        }
        
        // Проверка, что дата рождения не позже даты отсчета
        if (!newErrors.birthdayDate && !newErrors.referenceDate && birthdayObj > referenceObj) {
          newErrors.birthdayDate = 'Дата рождения должна быть раньше даты отсчета';
        }
      } catch (e) {
        newErrors.general = 'Ошибка при обработке дат';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета
  const calculateResults = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        let calculationResults = null;

        // Стандартный расчет разницы дат
        if (formData.calculationType === 'standard') {
          const startDateObj = new Date(formData.startDate);
          const endDateObj = new Date(formData.endDate);
          
          // Устанавливаем время в полночь для корректного расчета
          startDateObj.setHours(0, 0, 0, 0);
          endDateObj.setHours(0, 0, 0, 0);
          
          // Если включен параметр "включая конечную дату", добавляем 1 день к результату
          let endDateForCalc = endDateObj;
          if (formData.includeEnd) {
            endDateForCalc = new Date(endDateObj);
            endDateForCalc.setDate(endDateForCalc.getDate() + 1);
          }
          
          calculationResults = calculateDateDifference(startDateObj, endDateForCalc);
          
          // Добавляем информацию о датах
          calculationResults.startDate = startDateObj;
          calculationResults.endDate = endDateObj;
          calculationResults.includeEnd = formData.includeEnd;
        }
        
        // Расчет рабочих дней
        else if (formData.calculationType === 'working') {
          const startDateObj = new Date(formData.startDate);
          const endDateObj = new Date(formData.endDate);
          
          // Устанавливаем время в полночь для корректного расчета
          startDateObj.setHours(0, 0, 0, 0);
          endDateObj.setHours(0, 0, 0, 0);
          
          // Собираем праздники для выбранного региона
          let regionHolidays = [];
          if (formData.includeHolidays) {
            // Фильтруем федеральные праздники и региональные для выбранного региона
            regionHolidays = holidays.filter(h => 
              h.type === 'federal' || h.region === formData.regionCode
            ).map(h => new Date(h.date));
          }
          
          // Расчет рабочих дней
          const workingDaysCount = getWorkingDaysBetween(
            startDateObj,
            endDateObj,
            formData.includeEnd,
            regionHolidays
          );
          
          // Расчет общего количества дней
          const totalDaysCalc = calculateDateDifference(startDateObj, endDateObj);
          
          calculationResults = {
            ...totalDaysCalc,
            workingDays: workingDaysCount,
            startDate: startDateObj,
            endDate: endDateObj,
            includeEnd: formData.includeEnd,
            holidays: regionHolidays,
            regionCode: formData.regionCode,
            region: russianRegions.find(r => r.code === formData.regionCode)?.name || 'Россия'
          };
        }
        
        // Расчет добавления/вычитания дат
        else if (formData.calculationType === 'add-subtract') {
          const startDateObj = new Date(formData.startDate);
          const datePeriod = parseInt(formData.datePeriod);
          const periodType = formData.periodType;
          const operation = formData.operation;
          
          let resultDate = new Date(startDateObj);
          
          // Расчет в зависимости от типа периода
          switch (periodType) {
            case 'days':
              const daysToChange = operation === 'add' ? datePeriod : -datePeriod;
              
              if (formData.onlyWorkingDays) {
                // Сложнее с рабочими днями - нужно подсчитывать день за днем
                let daysAdded = 0;
                let currentDate = new Date(startDateObj);
                
                while (daysAdded < Math.abs(datePeriod)) {
                  currentDate.setDate(currentDate.getDate() + (operation === 'add' ? 1 : -1));
                  
                  // Проверяем, является ли день рабочим
                  const dayOfWeek = currentDate.getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  
                  let isHoliday = false;
                  if (formData.includeHolidays) {
                    isHoliday = holidays.some(h => {
                      const holidayDate = new Date(h.date);
                      return holidayDate.getFullYear() === currentDate.getFullYear() &&
                             holidayDate.getMonth() === currentDate.getMonth() &&
                             holidayDate.getDate() === currentDate.getDate();
                    });
                  }
                  
                  if (!isWeekend && !isHoliday) {
                    daysAdded++;
                  }
                }
                
                resultDate = currentDate;
              } else {
                // Простое добавление/вычитание календарных дней
                resultDate.setDate(resultDate.getDate() + daysToChange);
              }
              break;
              
            case 'weeks':
              const weeksToChange = (operation === 'add' ? datePeriod : -datePeriod) * 7;
              resultDate.setDate(resultDate.getDate() + weeksToChange);
              break;
              
            case 'months':
              const monthsToChange = operation === 'add' ? datePeriod : -datePeriod;
              resultDate.setMonth(resultDate.getMonth() + monthsToChange);
              break;
              
            case 'years':
              const yearsToChange = operation === 'add' ? datePeriod : -datePeriod;
              resultDate.setFullYear(resultDate.getFullYear() + yearsToChange);
              break;
              
            default:
              throw new Error(`Неподдерживаемый тип периода: ${periodType}`);
          }
          
          calculationResults = {
            startDate: startDateObj,
            resultDate: resultDate,
            operation: operation,
            datePeriod: datePeriod,
            periodType: periodType,
            onlyWorkingDays: formData.onlyWorkingDays,
            includeHolidays: formData.includeHolidays,
            formattedStartDate: formatDate(startDateObj, 'long'),
            formattedResultDate: formatDate(resultDate, 'long'),
            formattedPeriod: `${datePeriod} ${formatPeriodName(datePeriod, periodType)}`
          };
        }
        
        // Расчет возраста
        else if (formData.calculationType === 'age') {
          // Проверяем наличие необходимых данных
          if (!formData.birthdayDate || !formData.referenceDate) {
            throw new Error("Необходимо указать дату рождения и дату отсчета");
          }
          
          const birthdayObj = new Date(formData.birthdayDate);
          const referenceObj = new Date(formData.referenceDate);
          
          // Проверяем валидность дат
          if (isNaN(birthdayObj.getTime()) || isNaN(referenceObj.getTime())) {
            throw new Error("Некорректный формат даты");
          }
          
          // Устанавливаем время в полночь для корректного расчета
          birthdayObj.setHours(0, 0, 0, 0);
          referenceObj.setHours(0, 0, 0, 0);
          
          // Разница в миллисекундах
          const diffMs = Math.abs(referenceObj - birthdayObj);
          
          // Разница в днях
          const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
          
          // Расчет лет и оставшихся месяцев
          let years = referenceObj.getFullYear() - birthdayObj.getFullYear();
          let months = referenceObj.getMonth() - birthdayObj.getMonth();
          
          // Корректировка, если день в текущем месяце меньше дня рождения
          if (referenceObj.getDate() < birthdayObj.getDate()) {
            months--;
          }
          
          // Корректировка, если месяцы отрицательные
          if (months < 0) {
            years--;
            months += 12;
          }
          
          // Расчет оставшихся дней
          let days = referenceObj.getDate() - birthdayObj.getDate();
          if (days < 0) {
            const lastDayOfMonth = new Date(referenceObj.getFullYear(), referenceObj.getMonth(), 0).getDate();
            days += lastDayOfMonth;
          }
          
          // Расчет полных недель и месяцев
          const totalWeeks = Math.floor(totalDays / 7);
          const totalMonths = years * 12 + months;
          
          // Следующий день рождения
          let nextBirthday = new Date(referenceObj);
          nextBirthday.setMonth(birthdayObj.getMonth());
          nextBirthday.setDate(birthdayObj.getDate());
          
          if (
            nextBirthday.getMonth() < referenceObj.getMonth() || 
            (nextBirthday.getMonth() === referenceObj.getMonth() && nextBirthday.getDate() < referenceObj.getDate())
          ) {
            nextBirthday.setFullYear(nextBirthday.getFullYear() + 1);
          }
          
          // Дней до следующего дня рождения
          const daysToBirthday = Math.ceil((nextBirthday - referenceObj) / (1000 * 60 * 60 * 24));
          
          // Форматирование строки возраста
          const formattedAge = formatAgeString(years, months, days);
          
          calculationResults = {
            birthdayDate: birthdayObj,
            referenceDate: referenceObj,
            years: years,
            months: months,
            days: days,
            totalDays: totalDays,
            totalWeeks: totalWeeks,
            totalMonths: totalMonths,
            nextBirthday: nextBirthday,
            daysToBirthday: daysToBirthday,
            formattedBirthday: formatDate(birthdayObj, 'long'),
            formattedReferenceDate: formatDate(referenceObj, 'long'),
            zodiacSign: getZodiacSign(birthdayObj),
            chineseSign: getChineseZodiacSign(birthdayObj),
            isLeapYearBorn: isLeapYear(birthdayObj.getFullYear()),
            formattedAge: formattedAge
          };
        }

        setResults(calculationResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 100);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, holidays, russianRegions]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      includeEnd: true,
      calculationType: 'standard',
      includeHolidays: true,
      regionCode: 'RU-MOW',
      operation: 'add',
      datePeriod: 7,
      periodType: 'days',
      onlyWorkingDays: false,
      birthdayDate: new Date(new Date().setFullYear(new Date().getFullYear() - 30)).toISOString().split('T')[0],
      referenceDate: new Date().toISOString().split('T')[0],
    });
    setResults(null);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    let csvContent = '\uFEFF'; // UTF-8 BOM
    let fileName = 'расчет_дат.csv';

    // Формируем данные для разных типов расчетов
    if (formData.calculationType === 'standard' || formData.calculationType === 'working') {
      csvContent += 'Параметр;Значение\n';
      csvContent += `Начальная дата;${new Date(results.startDate).toLocaleDateString('ru-RU')}\n`;
      csvContent += `Конечная дата;${new Date(results.endDate).toLocaleDateString('ru-RU')}\n`;
      csvContent += `Включая конечную дату;${results.includeEnd ? 'Да' : 'Нет'}\n\n`;
      csvContent += 'Результаты\n';
      csvContent += `Дней;${results.days}\n`;
      csvContent += `Недель;${results.weeks}\n`;
      csvContent += `Месяцев;${results.months}\n`;
      csvContent += `Лет;${results.years}\n`;
      
      if (formData.calculationType === 'working') {
        csvContent += `Рабочих дней;${results.workingDays}\n`;
        csvContent += `Регион;${results.region}\n`;
      }
      
      fileName = 'расчет_промежутка_времени.csv';
    } 
    else if (formData.calculationType === 'add-subtract') {
      csvContent += 'Параметр;Значение\n';
      csvContent += `Начальная дата;${new Date(results.startDate).toLocaleDateString('ru-RU')}\n`;
      csvContent += `Операция;${results.operation === 'add' ? 'Добавление' : 'Вычитание'}\n`;
      csvContent += `Значение;${results.datePeriod} ${results.formattedPeriod}\n`;
      csvContent += `Только рабочие дни;${results.onlyWorkingDays ? 'Да' : 'Нет'}\n\n`;
      csvContent += 'Результаты\n';
      csvContent += `Результирующая дата;${new Date(results.resultDate).toLocaleDateString('ru-RU')}\n`;
      
      fileName = 'расчет_операции_с_датой.csv';
    }
    else if (formData.calculationType === 'age') {
      csvContent += 'Параметр;Значение\n';
      csvContent += `Дата рождения;${new Date(results.birthdayDate).toLocaleDateString('ru-RU')}\n`;
      csvContent += `Дата отсчета;${new Date(results.referenceDate).toLocaleDateString('ru-RU')}\n\n`;
      csvContent += 'Результаты\n';
      csvContent += `Возраст;${results.formattedAge}\n`;
      csvContent += `Полных лет;${results.years}\n`;
      csvContent += `Полных месяцев;${results.totalMonths}\n`;
      csvContent += `Полных недель;${results.totalWeeks}\n`;
      csvContent += `Дней;${results.totalDays}\n`;
      csvContent += `До следующего дня рождения;${results.daysToBirthday} дн.\n`;
      csvContent += `Знак зодиака;${results.zodiacSign}\n`;
      csvContent += `Китайский гороскоп;${results.chineseSign}\n`;
      
      fileName = 'расчет_возраста.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateResults();
  };

  // Получение знака зодиака
  const getZodiacSign = (date) => {
    const day = date.getDate();
    const month = date.getMonth() + 1;

    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 'Овен';
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 'Телец';
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 'Близнецы';
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 'Рак';
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'Лев';
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'Дева';
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 'Весы';
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 'Скорпион';
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 'Стрелец';
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return 'Козерог';
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 'Водолей';
    return 'Рыбы';
  };

  // Получение знака китайского гороскопа
  const getChineseZodiacSign = (date) => {
    const year = date.getFullYear();
    const animals = ['Крыса', 'Бык', 'Тигр', 'Кролик', 'Дракон', 'Змея', 'Лошадь', 'Коза', 'Обезьяна', 'Петух', 'Собака', 'Свинья'];
    return animals[(year - 4) % 12];
  };

  // Проверка високосного года
  const isLeapYear = (year) => {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  };

  // Форматирование строки возраста
  const formatAgeString = (years, months, days) => {
    let result = '';
    
    if (years > 0) {
      result += `${years} ${pluralize(years, 'год', 'года', 'лет')}`;
    }
    
    if (months > 0) {
      result += result ? ' ' : '';
      result += `${months} ${pluralize(months, 'месяц', 'месяца', 'месяцев')}`;
    }
    
    if (days > 0 || (years === 0 && months === 0)) {
      result += result ? ' ' : '';
      result += `${days} ${pluralize(days, 'день', 'дня', 'дней')}`;
    }
    
    return result;
  };

  // Форматирование названия периода
  const formatPeriodName = (count, type) => {
    switch (type) {
      case 'days':
        return pluralize(count, 'день', 'дня', 'дней');
      case 'weeks':
        return pluralize(count, 'неделя', 'недели', 'недель');
      case 'months':
        return pluralize(count, 'месяц', 'месяца', 'месяцев');
      case 'years':
        return pluralize(count, 'год', 'года', 'лет');
      default:
        return '';
    }
  };

  // Склонение слов
  const pluralize = (count, one, few, many) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return many;
    }
    
    if (lastDigit === 1) {
      return one;
    }
    
    if (lastDigit >= 2 && lastDigit <= 4) {
      return few;
    }
    
    return many;
  };

  // Иконка для типа расчета
  const getCalculationTypeIcon = () => {
    switch (formData.calculationType) {
      case 'standard':
        return <Calendar className="w-5 h-5 text-cyan-500" />;
      case 'working':
        return <Briefcase className="w-5 h-5 text-cyan-500" />;
      case 'add-subtract':
        return formData.operation === 'add' 
          ? <Plus className="w-5 h-5 text-cyan-500" /> 
          : <Minus className="w-5 h-5 text-cyan-500" />;
      case 'age':
        return <User className="w-5 h-5 text-cyan-500" />;
      default:
        return <Calendar className="w-5 h-5 text-cyan-500" />;
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <CalendarRange className="w-6 h-6 text-cyan-500" />
          Калькулятор дат
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Выбор типа расчета */}
        <div className="mb-6">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип расчета
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <label className={`
              flex flex-col items-center text-center p-3 rounded-lg border cursor-pointer transition-colors
              ${formData.calculationType === 'standard' 
                ? 'bg-cyan-50 border-cyan-300 dark:bg-cyan-900/30 dark:border-cyan-700' 
                : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600'
              }
            `}>
              <input
                type="radio"
                name="calculationType"
                value="standard"
                checked={formData.calculationType === 'standard'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <Calendar className="w-6 h-6 mb-2 text-cyan-600 dark:text-cyan-400" />
              <span className="text-sm font-medium">Разница дат</span>
            </label>
            
            <label className={`
              flex flex-col items-center text-center p-3 rounded-lg border cursor-pointer transition-colors
              ${formData.calculationType === 'working' 
                ? 'bg-cyan-50 border-cyan-300 dark:bg-cyan-900/30 dark:border-cyan-700' 
                : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600'
              }
            `}>
              <input
                type="radio"
                name="calculationType"
                value="working"
                checked={formData.calculationType === 'working'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <Briefcase className="w-6 h-6 mb-2 text-cyan-600 dark:text-cyan-400" />
              <span className="text-sm font-medium">Рабочие дни</span>
            </label>
            
            <label className={`
              flex flex-col items-center text-center p-3 rounded-lg border cursor-pointer transition-colors
              ${formData.calculationType === 'add-subtract' 
                ? 'bg-cyan-50 border-cyan-300 dark:bg-cyan-900/30 dark:border-cyan-700' 
                : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600'
              }
            `}>
              <input
                type="radio"
                name="calculationType"
                value="add-subtract"
                checked={formData.calculationType === 'add-subtract'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <Plus className="w-6 h-6 mb-2 text-cyan-600 dark:text-cyan-400" />
              <span className="text-sm font-medium">Добавить/вычесть</span>
            </label>
            
            <label className={`
              flex flex-col items-center text-center p-3 rounded-lg border cursor-pointer transition-colors
              ${formData.calculationType === 'age' 
                ? 'bg-cyan-50 border-cyan-300 dark:bg-cyan-900/30 dark:border-cyan-700' 
                : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600'
              }
            `}>
              <input
                type="radio"
                name="calculationType"
                value="age"
                checked={formData.calculationType === 'age'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <User className="w-6 h-6 mb-2 text-cyan-600 dark:text-cyan-400" />
              <span className="text-sm font-medium">Расчет возраста</span>
            </label>
          </div>
        </div>

        {/* Поля для стандартного расчета и расчета рабочих дней */}
        {(formData.calculationType === 'standard' || formData.calculationType === 'working') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-cyan-500" />
                Начальная дата
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.startDate ? 'input-error' : ''}`}
              />
              {errors.startDate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.startDate}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-cyan-500" />
                Конечная дата
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.endDate ? 'input-error' : ''}`}
              />
              {errors.endDate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.endDate}
                </p>
              )}
            </div>

            <div className="col-span-full">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="includeEnd"
                  checked={formData.includeEnd}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Включать конечную дату в расчет
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                При включении конечной даты, она будет учтена в расчете как полный день
              </p>
            </div>
          </div>
        )}

        {/* Поля для расчета рабочих дней */}
        {formData.calculationType === 'working' && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <Briefcase className="w-4 h-4 mr-2 text-blue-500" />
              Параметры рабочих дней
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="regionCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Регион (для праздников)
                </label>
                <select
                  id="regionCode"
                  name="regionCode"
                  value={formData.regionCode}
                  onChange={handleInputChange}
                  className={`form-select w-full ${errors.regionCode ? 'input-error' : ''}`}
                >
                  {russianRegions.map((region) => (
                    <option key={region.code} value={region.code}>
                      {region.name}
                    </option>
                  ))}
                </select>
                {errors.regionCode && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.regionCode}
                  </p>
                )}
              </div>

              <div className="flex items-center">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="includeHolidays"
                    checked={formData.includeHolidays}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Учитывать праздничные дни
                  </span>
                </label>
              </div>
            </div>
            
            <p className="mt-3 text-xs text-blue-600 dark:text-blue-400">
              Рабочими днями считаются будни (понедельник-пятница), исключая праздничные дни. 
              Учитываются федеральные праздники РФ и переносы выходных дней согласно производственному календарю.
            </p>
          </div>
        )}

        {/* Поля для добавления/вычитания дат */}
        {formData.calculationType === 'add-subtract' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-cyan-500" />
                Исходная дата
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.startDate ? 'input-error' : ''}`}
              />
              {errors.startDate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.startDate}
                </p>
              )}
            </div>

            <div>
              <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Операция
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className={`
                  inline-flex items-center justify-center p-2 rounded-lg border transition-colors cursor-pointer
                  ${formData.operation === 'add' 
                    ? 'bg-green-50 border-green-300 dark:bg-green-900/30 dark:border-green-700' 
                    : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600'
                  }
                `}>
                  <input
                    type="radio"
                    name="operation"
                    value="add"
                    checked={formData.operation === 'add'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <Plus className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium">Добавить</span>
                </label>
                
                <label className={`
                  inline-flex items-center justify-center p-2 rounded-lg border transition-colors cursor-pointer
                  ${formData.operation === 'subtract' 
                    ? 'bg-red-50 border-red-300 dark:bg-red-900/30 dark:border-red-700' 
                    : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600'
                  }
                `}>
                  <input
                    type="radio"
                    name="operation"
                    value="subtract"
                    checked={formData.operation === 'subtract'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <Minus className="w-4 h-4 mr-2 text-red-600 dark:text-red-400" />
                  <span className="text-sm font-medium">Вычесть</span>
                </label>
              </div>
            </div>

            <div>
              <label htmlFor="datePeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Количество
              </label>
              <input
                type="number"
                id="datePeriod"
                name="datePeriod"
                min="1"
                value={formData.datePeriod}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.datePeriod ? 'input-error' : ''}`}
              />
              {errors.datePeriod && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.datePeriod}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="periodType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Единица измерения
              </label>
              <select
                id="periodType"
                name="periodType"
                value={formData.periodType}
                onChange={handleInputChange}
                className={`form-select w-full ${errors.periodType ? 'input-error' : ''}`}
              >
                <option value="days">Дней</option>
                <option value="weeks">Недель</option>
                <option value="months">Месяцев</option>
                <option value="years">Лет</option>
              </select>
              {errors.periodType && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.periodType}
                </p>
              )}
            </div>

            <div className="col-span-full">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="onlyWorkingDays"
                  checked={formData.onlyWorkingDays}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Учитывать только рабочие дни
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Применимо только для периода в днях. Выходные и праздники не будут учитываться при расчете.
              </p>
            </div>
          </div>
        )}

        {/* Поля для расчета возраста */}
        {formData.calculationType === 'age' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="birthdayDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <User className="w-4 h-4 mr-2 text-cyan-500" />
                Дата рождения
              </label>
              <input
                type="date"
                id="birthdayDate"
                name="birthdayDate"
                value={formData.birthdayDate || ''}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.birthdayDate ? 'input-error' : ''}`}
              />
              {errors.birthdayDate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.birthdayDate}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="referenceDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-cyan-500" />
                Дата отсчета
              </label>
              <input
                type="date"
                id="referenceDate"
                name="referenceDate"
                value={formData.referenceDate}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.referenceDate ? 'input-error' : ''}`}
              />
              {errors.referenceDate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.referenceDate}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                По умолчанию текущая дата, можно выбрать любую другую для расчета возраста на определенный момент
              </p>
            </div>
          </div>
        )}

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={getCalculationTypeIcon()}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Вычисляем...' : 'Рассчитать промежуток'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать CSV
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;